
import {db} from "@/firebase"
import {doc, collection, getDoc, getDocs, setDoc, addDoc, updateDoc, deleteDoc, where, query, serverTimestamp,Timestamp, orderBy, limit, startAfter} from "firebase/firestore"
import {NewCompanyProfile} from "@/entities"
import {CompanyProfile, CompanyProfileConverter} from "@/core/converters/company_profile"
import { CompanyConverter } from "@/core/converters/company"
import {User, UserConverter} from "@/core/converters/user"

const CompaniesState = {
  companies: {},
  companyProfiles: {},
  companyProposalRequests: {},
  company_contacts: {}, // company_id: {contact_id: contact}
  company_users: {}, // company_id: {user_id: user}
  lastLoadedContactDocument: null,
}

const CompaniesMutations = {
  setCompanies(state, companies){
    state.companies = companies
  },
  updateCompany(state, company){
    state.companies[company.company_id] = company
  },
  setCompanyProfile(state, profile){
    state.companyProfiles[profile.company_profile_id] = profile
  },
  updateCompanyProfile(state, profile){
    state.companyProfiles[profile.company_profile_id] = profile
  },
  updateProposalRequests(state, request){
    state.companyProposalRequests[request.proposal_request_id] = request
  },
  setCompanyContact(state, contact){
    if (!contact)
    if (!state.company_contacts[contact.source]){
      state.company_contacts[contact.source] = {}
      state.lastLoadedContactDocument = null
    }
    state.company_contacts[contact.source][contact.id] = contact
  },
  setLastLoadedContactDocument(state, doc){
    state.lastLoadedContactDocument = doc
  },
  setCompanyUser(state, user){
    if (!state.company_users[user.company_id]){
      state.company_users[user.company_id] = {}
    }
    state.company_users[user.company_id][user.user_id] = user
  }
}

const CompaniesActions = {
  // getCompanies retrieves all companies from the collection
  // if state.companies is empty, otherwise it only retrieves
  // companies that have been updated since the last time
  async getCompanies({commit, state}){
    let collectionRef = collection(db, "companies").withConverter(CompanyConverter)
    let w = null;
    let q = (ref, where=null) => w ? getDocs(query(ref, w)) : getDocs(ref)
    if (Object.keys(state.companies).length > 0){
      let last_updated = Object.values(state.companies).sort((a,b) => new Date(b.updated_time) - new Date(a.updated_time))[0].updated_time
      w = where("updated_time", ">", Timestamp.fromDate(new Date(last_updated)))
      console.log("getting companies updated since " + last_updated)
    }

    let snapshots = await q(collectionRef, w)
    snapshots.forEach((doc) => {
      let id = doc.id
      let company = doc.data()
      company.company_id = id
      commit("updateCompany", company)
    })
  },
  async loadCompanyProfile({commit, state}, company_id){
    if (state.companyProfiles[company_id]){
      let profile = state.companyProfiles[company_id]
      if (new Date() - new Date(profile.updated_time) < 1000 * 60 * 5) {
        return profile
      }
    }

    let collectionRef = collection(db, "company_profile").withConverter(CompanyProfileConverter)
    let docRef = doc(collectionRef, company_id)
    let docSnap = await getDoc(docRef)
    if (!docSnap.exists())return 
    
    let profile = docSnap.data()
    commit("setCompanyProfile", profile)
  },

  async loadCompanyProfiles({commit, state}){
    let latest = Object.values(state.companyProfiles).sort((a,b) => new Date(b.updated_time) - new Date(a.updated_time))[0]
    let collectionRef = collection(db, "company_profile").withConverter(CompanyProfileConverter)
    let conds = []
    if (latest && new Date(latest.updated_time) != "Invalid Date"){
      console.log(latest.updated_time)
      conds.push(where("updated_time", ">", Timestamp.fromDate(new Date(latest.updated_time))))
    } else if (latest){
      console.log(`latest updated time (${latest.updated_time}) is invalid`)
    }
    let snapshots = await getDocs(query(collectionRef, ...conds))
    snapshots.forEach((doc) => {
      let profile = doc.data()
      commit("setCompanyProfile", profile)
    })
  },

  async getCompany({commit, state, dispatch}, company_id){
    let company = state.companies[company_id]
    if (company) return company

    let collectionRef = collection(db, "companies").withConverter(CompanyConverter)
    let docRef = doc(collectionRef, company_id)
    let docSnap = await getDoc(docRef)
    if (docSnap.exists()){
      let company = docSnap.data()
      company.company_id = docSnap.id
      commit("updateCompany", company)
    }
    return company
  },



  async addCompany({commit, state}, company){
    let collectionRef = collection(db, "companies").withConverter(CompanyConverter)
    let characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let companyCode = '';
    for (let i = 0; i < 10; i++) {
      companyCode += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    company.company_code = companyCode;
    let docRef = await addDoc(collectionRef, company)
    company.company_id = docRef.id
    commit("updateCompany", company)

    let profile = new CompanyProfile(company.company_id, company.name)
    collectionRef = collection(db, "company_profile").withConverter(CompanyProfileConverter)
    docRef = doc(collectionRef, company.company_id)
    await setDoc(docRef, profile)
    commit("setCompanyProfile", profile)

    return company
  },

  async lookupCompanyByCode({commit, state}, company_code){
    let company = Object.values(state.companies).find((company) => company.company_code == company_code)
    if (company) return company

    let collectionRef = collection(db, "companies").withConverter(CompanyConverter)
    let snapshots 
    try {
      snapshots =  await getDocs(query(collectionRef, where("company_code", "==", String(company_code)), limit(1)))
    } catch (error){
      // console.error("lookup error", error)
      if (error.code == "permission-denied") return null
      else {
        console.error("lookup error", error)
        return null
      }
    }
    snapshots.forEach((doc) => {
      company = doc.data()
    })
    if (company) commit("updateCompany", company)
    return company
  },
  
  async deleteCompany({commit, state}, company){
    let collectionRef = collection(db, "companies")
    let docRef = doc(collectionRef, company.company_id)
    await deleteDoc(docRef)
    commit("deleteCompany", company)
  },

  async getCompanyProposalRequests({commit, state}, company_id){
    let latest = Object.values(state.companyProposalRequests).sort((a,b) => new Date(b.updated_time) - new Date(a.updated_time))[0]
    let collectionRef = collection(db, "proposal_requests").withConverter(ProposalRequestConverter)
    let conds = [where("company_id", "==", company_id)]
    if (latest) conds.push(where("updated_time", ">", Timestamp.fromDate(new Date(latest.updated_time))))
    let snapshots = await getDocs(query(collectionRef, ...conds))
    console.log(`got (${snapshots.docs.length}) proposal requests for company ` + company_id)
    snapshots.docs.forEach((doc) => {
      let request = doc.data()
      commit("updateProposalRequests", request)
    })
  },

  async loadCompanyContacts({commit, state}, since=null){
    if (!state.user_claims || !state.user_claims.company_id) return;
    if (!state.company_contacts[state.user_claims.company_id]){
      state.company_contacts = {}
      state.company_contacts[state.user_claims.company_id] = {}
      state.lastLoadedContactDocument = null
    }

    let company_id = state.user_claims.company_id;
    let conds = [where("source", "==", company_id)];
  
    if (!state.user_claims.company_admin) {
      conds.push(where("source_user_id", "==", state.user_claims.user_id));
    }

    if (since == null){
      // 7 days ago
      since = new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 7)
    }

    if (since != null){
      // check if we data before this date
      let earliest = new Date();
      let contacts = Object.values(state.company_contacts[company_id])
      for (let i = 0; i < contacts.length; i++){
        if (new Date(contacts[i].updated_at) < earliest) earliest = new Date(contacts[i].updated_at)
        if (new Date(contacts[i].updated_at) < new Date(since)) return
      }
      conds.push(where("updated_at", "<", Timestamp.fromDate(earliest)))
      conds.push(where("updated_at", ">", Timestamp.fromDate(new Date(since))))
    }
  
    let collectionRef = collection(db, "contacts").withConverter(ContactConverter);
    let queryRef = query(collectionRef, ...conds, orderBy("updated_at", "desc"));  
    let snapshots = await getDocs(queryRef);
    let documents = snapshots.docs;
    documents.forEach((doc) => {
      let contact = doc.data();
      commit("setCompanyContact", contact);
    });
  },

  async loadCompanyUser({commit, state}, user_id){
    let user = Object.values(state.company_users).find((user) => user.user_id == user_id)
    if (user) return user

    try {
      let collectionRef = collection(db, "users").withConverter(UserConverter)
      let snapshots = await getDoc(doc(collectionRef, user_id))
      let user = snapshots.data()
      commit("setCompanyUser", user)
      return user
    }catch (error){
      return new User()
    }
  }
  
  






}

const CompaniesGetters = {
  getCompanyByCode: (state) => (code) => {
    return Object.values(state.companies).find((company) => company.company_code == code)
  },
  getContactProposalRequests: (state) => (contact_id) => {
    return Object.values(state.companyProposalRequests).filter((request) => request.contact_id == contact_id)
  },
  getCompanyProfile: (state) => (company_id) => {
    return state.companyProfiles[company_id]
  },
  getProposalRequest: (state) => (request_id) => {
    return state.companyProposalRequests[request_id]
  }
}

export {CompaniesState, CompaniesMutations, CompaniesActions, CompaniesGetters}